import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * EntityDetailRail — the *attached-rail* scaffold for a detail page: a flush,
 * full-height left rail beside a scrolling work area, with a single hairline
 * divider between them. The sibling of {@link EntityDetailLayout}, which floats
 * the panel as a bordered card on a gutter'd canvas.
 *
 *   <EntityDetailRail rail={<DetailPanel variant="rail">…sections…</DetailPanel>}>
 *     <DetailHeader eyebrow="Contact" title="Evan Reid" … />
 *     …tabs · activity feed…
 *   </EntityDetailRail>
 *
 * **Why a rail (vs. the floating card).** A detail page has a clear
 * primary/secondary split — the record's summary/metadata (reference) vs. the
 * activity/work area (focus). A flush rail expresses that hierarchy
 * *structurally*: the rail reads as persistent chrome you scan, the work area
 * reads as the document you act in. It also reclaims the outer gutter + the
 * card's border/radius/shadow, so there's more room and less visual noise. This
 * is the CRM / inbox / IDE convention (Salesforce, Gmail, Linear, VS Code).
 * Floating cards ({@link EntityDetailLayout}) stay the right idiom for
 * list/dashboard surfaces where several peer cards share a canvas.
 *
 * **Layout.** Fills its parent's height (requires a height-bounded parent, e.g.
 * `h-full`) and gives each column its own scroll: the rail via its own
 * `overflow` (a `DetailPanel variant="rail"`), the work area here. The record
 * header lives in the work area (as `children`), not the rail, so the page reads
 * as *one record with a reference rail* — not two competing sidebars. The rail
 * brings its own surface + right divider; the work area is transparent, so the
 * page's canvas colour (e.g. a subtle sunken gray) shows through and the rail's
 * white reads as chrome against it.
 */
export interface EntityDetailRailProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The left rail — typically a `<DetailPanel variant="rail">`. Rendered flush
   * to the layout's left edge, full height, scrolling internally.
   */
  rail: React.ReactNode
  /**
   * Main work area — a `DetailHeader` + the record's body (tabs / activity).
   * Scrolls independently of the rail.
   */
  children: React.ReactNode
  /** Classes for the scrolling work-area column (e.g. adjust the default padding). */
  contentClassName?: string
}

export const EntityDetailRail = React.forwardRef<
  HTMLDivElement,
  EntityDetailRailProps
>(({ rail, children, contentClassName, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex h-full min-h-0', className)}
    {...props}
  >
    {rail}
    <div
      className={cn(
        // Own scroll for the work area. Generous padding doubles as the inset
        // that keeps a focus ring (e.g. the header's Actions button) off the
        // scroll container's clipped edge.
        'fbui-scroll min-w-0 flex-1 overflow-y-auto p-6',
        contentClassName
      )}
    >
      {children}
    </div>
  </div>
))
EntityDetailRail.displayName = 'EntityDetailRail'
